import { Alert, Box, Col, Row } from '@ksyos/design-system';
import { useState } from 'react';
import type { IAsset } from '../../../../../common/validation/assets';
import { translateKey } from '../../../../translate';
import { AssetDropzone } from '../assetDropzone/AssetDropzone';
import { AssetThumbnail } from '../assetDropzone/AssetThumbnail';

interface IAssetDropzoneWithThumbnailsProps {
    acceptedFiles?: string;
    alternativeUrl?: string;
    files: IAsset[];
    handleChange?: (files: IAsset[]) => void;
    id?: number;
    limit?: number;
    onlyThumbnails?: boolean;
    onUploadStatusChanged?: (isUploading: boolean) => void;
    placeholder?: string;
    showFilenames?: boolean;
}

export function AssetDropzoneWithThumbnails(props: IAssetDropzoneWithThumbnailsProps) {
    const limit = props.limit || 1;

    const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string | null>(null);
    const [initialFiles, setInitialFiles] = useState<IAsset[]>([...props.files]);
    const [currentLimit, setCurrentLimit] = useState<number>(limit - props.files.length);

    function onRemoveInitialFile(s3Id: string) {
        const initialFilesUpdate = initialFiles.filter((file) => file.s3Id !== s3Id);

        setInitialFiles(initialFilesUpdate);
        setCurrentLimit(limit - initialFilesUpdate.length);

        handleFileRemoved(s3Id);
    }

    function handleFileRemoved(s3Id: string) {
        if (props.handleChange) {
            props.handleChange(props.files.filter((file) => file.s3Id !== s3Id));
        }
    }

    return (
        <div id={`asset-dropzone-${props.id}`}>
            {fileUploadErrorMessage && (
                <Alert appearance="warning">{`${translateKey(
                    'dropzone.uploadError'
                )}: ${fileUploadErrorMessage}`}</Alert>
            )}

            {!props.onlyThumbnails && initialFiles.length < limit && (
                <Row>
                    <Col span={12}>
                        <AssetDropzone
                            acceptedFiles={props.acceptedFiles || '.png,.jpeg,.jpg'}
                            alternativeUrl={props.alternativeUrl}
                            clearAfterUpload
                            dragFilesText={translateKey('dropzone.dragFiles')}
                            key="patientPictureUpload"
                            maxFiles={currentLimit}
                            onComplete={() => {
                                if (props.onUploadStatusChanged) {
                                    const thumbnailContainers = document.querySelectorAll('.dz-details.placeholder');
                                    const thumbnailImages: HTMLImageElement[] = [];
                                    for (const thumbnail of thumbnailContainers) {
                                        thumbnailImages.push(thumbnail.children[0] as HTMLImageElement);
                                    }
                                    if (thumbnailImages.every((img) => img.complete)) {
                                        props.onUploadStatusChanged(false);
                                    } else {
                                        for (const image of thumbnailImages) {
                                            if (!image.complete) {
                                                image.addEventListener('load', (_event) => {
                                                    if (thumbnailImages.every((img) => img.complete)) {
                                                        props.onUploadStatusChanged?.(false);
                                                    }
                                                });
                                            }
                                        }
                                    }
                                }
                            }}
                            onError={(message) => setFileUploadErrorMessage(message)}
                            onFileUploadComplete={(file) => {
                                const addedFile = {
                                    filename: file.name,
                                    s3Id: file.s3Id,
                                    thumbnailDataUrl:
                                        file.previewElement.querySelector('img[data-dz-thumbnail=true]').src,
                                };

                                if (props.handleChange) {
                                    props.handleChange([...props.files, addedFile]);
                                }

                                setFileUploadErrorMessage(null);
                            }}
                            onRemoved={(file, isUploadInProgress) => {
                                handleFileRemoved(file.s3Id);
                                if (props.onUploadStatusChanged) {
                                    props.onUploadStatusChanged(isUploadInProgress);
                                }
                            }}
                            onSending={async () => {
                                if (props.onUploadStatusChanged) {
                                    props.onUploadStatusChanged(true);
                                }
                            }}
                            placeholder={props.placeholder}
                            setMaxAmountFiles={limit}
                        />
                    </Col>
                </Row>
            )}

            {initialFiles.length > 0 && (
                <Box
                    alignItems="center"
                    display="flex"
                    flexDirection="column"
                    gap={4}
                >
                    {initialFiles.map((file) => (
                        <AssetThumbnail
                            file={file}
                            key={file.s3Id}
                            onlyThumbnails={props.onlyThumbnails}
                            onThumbnailRemove={() => onRemoveInitialFile(file.s3Id)}
                            showFilenames={props.showFilenames}
                        />
                    ))}
                </Box>
            )}
        </div>
    );
}
